import { useMutation, useQuery } from '@apollo/client';
import { FC, Fragment, useContext, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Alert,
  Badge,
  Col,
  Input,
  InputGroup,
  InputGroupAddon,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import CLOSE_ICON from '../../../../assets/images/close.svg';
import EMPTY_ICON from '../../../../assets/images/empty-box.svg';
import { CrossOutlinedIcon, ReplyIcon } from '../../../../assets/images/svgs';
import ConfirmPopUp from '../../../../components/ConfirmPopUp';
import Loader from '../../../../components/layout/Loader';
import { AppContext } from '../../../../context';
import { ROLES } from '../../../../utils/constant';
import { moneyFormatter } from '../../../../utils/helper';
import {
  CLAWBACK_DH_PLACEMENT_COMMISSION,
  CREATE_DH_PLACEMENT_COMMISSION,
  GET_DH_PLACEMENT_COMMISSIONS,
  VOID_DH_PLACEMENT_COMMISSION,
} from '../gql';

// kan-90. The API stores lowercase enum values ('recruiting' / 'projected' / ...)
// but code-first GraphQL serializes the enum KEYS, so the browser always sees and
// must send the uppercase keys below — same as splitType 'RECRUITING' / 'ACCOUNT_MANAGER'.
type CommissionSide = 'RECRUITING' | 'AM';
type CommissionEntryType = 'COMMISSION' | 'CLAWBACK';
type CommissionStatus = 'PROJECTED' | 'EARNED' | 'VOID';
type CommissionRateType = 'PERCENTAGE' | 'FLAT_RATE';

interface IRateInput {
  type: CommissionRateType;
  value: string;
}

interface ICommissionUser {
  id: string;
  fullName: string;
}

interface IDHPlacementCommission {
  id: string;
  userId: string;
  commissionSide: CommissionSide;
  entryType: CommissionEntryType;
  baseAmount: number;
  splitPercentage: number;
  rateType: CommissionRateType;
  commissionRate: number | null;
  commissionAmount: number;
  status: CommissionStatus;
  relatedCommissionId: string | null;
  createdAt: string;
  user?: ICommissionUser | null;
  byUser?: ICommissionUser | null;
}

interface ICommissionParentNode {
  row: IDHPlacementCommission;
  clawbacks: Array<IDHPlacementCommission>;
}

interface ICommissionGroup {
  key: string;
  userId: string;
  side: CommissionSide;
  userName: string;
  parents: Array<ICommissionParentNode>;
  orphanClawbacks: Array<IDHPlacementCommission>;
  net: number;
  hasClawback: boolean;
}

interface ISplitOption {
  label: string;
  userName: string;
  value: string;
  userId: string;
  side: CommissionSide;
}

interface IDHPlacementCommissionsProps {
  placementId: string;
  placementSplitting: Array<any>;
  netCommission: number | null | undefined;
}

const sideLabel = (side: CommissionSide): string =>
  side === 'RECRUITING' ? 'Recruiting' : 'Account Manager';

const statusColor = (status: CommissionStatus): string => {
  if (status === 'EARNED') return 'success';
  if (status === 'PROJECTED') return 'warning';
  return 'secondary';
};

const statusLabel = (status: CommissionStatus): string => {
  if (status === 'EARNED') return 'Earned';
  if (status === 'PROJECTED') return 'Projected';
  return 'Void';
};

const isSuccessResponse = (status?: number): boolean =>
  typeof status === 'number' && status >= 200 && status < 300;

const DHPlacementCommissions: FC<IDHPlacementCommissionsProps> = ({
  placementId,
  placementSplitting,
  netCommission,
}) => {
  const { userRoles } = useContext(AppContext);
  const isExecutive = !!userRoles?.includes(ROLES.EXECUTIVE);

  const [addModal, setAddModal] = useState<boolean>(false);
  const [rateInputs, setRateInputs] = useState<Record<string, IRateInput>>({});
  const [addError, setAddError] = useState<string>('');
  const [batchSaving, setBatchSaving] = useState<boolean>(false);

  const [clawbackRow, setClawbackRow] = useState<IDHPlacementCommission | null>(
    null
  );
  const [clawbackAmount, setClawbackAmount] = useState<string>('');
  const [clawbackError, setClawbackError] = useState<string>('');

  const [voidRow, setVoidRow] = useState<IDHPlacementCommission | null>(null);

  const { data, loading, error, refetch } = useQuery(
    GET_DH_PLACEMENT_COMMISSIONS,
    {
      variables: { placementId },
      skip: !placementId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const [_createCommission, { loading: createLoading }] = useMutation(
    CREATE_DH_PLACEMENT_COMMISSION
  );
  const [_clawbackCommission, { loading: clawbackLoading }] = useMutation(
    CLAWBACK_DH_PLACEMENT_COMMISSION
  );
  const [_voidCommission, { loading: voidLoading }] = useMutation(
    VOID_DH_PLACEMENT_COMMISSION
  );

  const rows: Array<IDHPlacementCommission> = useMemo(
    () => data?.dhPlacementCommissions || [],
    [data?.dhPlacementCommissions]
  );

  const groups: Array<ICommissionGroup> = useMemo(() => {
    const activeRows = rows.filter((row) => row.status !== 'VOID');
    const groupMap = new Map<string, ICommissionGroup>();
    const parentMap = new Map<string, ICommissionParentNode>();

    const ensureGroup = (row: IDHPlacementCommission): ICommissionGroup => {
      const key = `${row.userId}|${row.commissionSide}`;
      let group = groupMap.get(key);
      if (!group) {
        group = {
          key,
          userId: row.userId,
          side: row.commissionSide,
          userName: row.user?.fullName || '--',
          parents: [],
          orphanClawbacks: [],
          net: 0,
          hasClawback: false,
        };
        groupMap.set(key, group);
      }
      return group;
    };

    for (const row of activeRows) {
      if (row.entryType !== 'COMMISSION') continue;
      const group = ensureGroup(row);
      const node: ICommissionParentNode = { row, clawbacks: [] };
      group.parents.push(node);
      parentMap.set(row.id, node);
    }

    for (const row of activeRows) {
      if (row.entryType !== 'CLAWBACK') continue;
      const group = ensureGroup(row);
      group.hasClawback = true;
      const parent = row.relatedCommissionId
        ? parentMap.get(row.relatedCommissionId)
        : undefined;
      // A clawback whose commission row was voided afterwards keeps its own row so
      // its (negative) amount stays visible and counted in the group's net.
      if (parent) {
        parent.clawbacks.push(row);
      } else {
        group.orphanClawbacks.push(row);
      }
    }

    for (const row of activeRows) {
      const group = groupMap.get(`${row.userId}|${row.commissionSide}`);
      if (group) {
        group.net += Number(row.commissionAmount) || 0;
      }
    }

    return Array.from(groupMap.values());
  }, [rows]);

  const splitOptions: Array<ISplitOption> = useMemo(() => {
    // A user+side that already has an active (projected/earned) commission is
    // excluded — the server rejects duplicates, so don't offer them. Voided
    // entries free the slot for re-entry.
    const covered = new Set(
      rows
        .filter(
          (row) => row.status !== 'VOID' && row.entryType === 'COMMISSION'
        )
        .map((row) => `${row.userId}|${row.commissionSide}`)
    );
    return (placementSplitting || [])
      .filter(
        (split) =>
          !!split?.user?.id &&
          (split?.splitType === 'RECRUITING' ||
            split?.splitType === 'ACCOUNT_MANAGER')
      )
      .filter((split) => {
        const side = split?.splitType === 'RECRUITING' ? 'RECRUITING' : 'AM';
        return !covered.has(`${split?.user?.id}|${side}`);
      })
      .map((split) => {
        const side: CommissionSide =
          split?.splitType === 'RECRUITING' ? 'RECRUITING' : 'AM';
        return {
          label: `${split?.user?.fullName} — ${sideLabel(side)}`,
          userName: split?.user?.fullName,
          value: `${split?.user?.id}|${side}`,
          userId: split?.user?.id,
          side,
        };
      })
      // Account managers first, then recruiters — matching the section order.
      .sort((a, b) => {
        if (a.side === b.side) return 0;
        return a.side === 'AM' ? -1 : 1;
      });
  }, [placementSplitting, rows]);

  // A $0 fee is a real (if worthless) fee — the server accepts it, so only a
  // truly missing netCommission blocks entry.
  const hasNetCommission =
    netCommission !== null && netCommission !== undefined;

  // kan-95: commission is decoupled from the billing-credit split — the rate is
  // applied to the side's half of the net fee, whatever the user's split % is.
  const previewFor = (input?: IRateInput): number => {
    const value = parseFloat(input?.value ?? '');
    if (isNaN(value)) return 0;
    if (input?.type === 'FLAT_RATE') return value;
    return (((Number(netCommission) || 0) / 2) * value) / 100;
  };

  const remainingClawback = (row: IDHPlacementCommission | null): number => {
    if (!row) return 0;
    const clawedBack = rows
      .filter(
        (item) =>
          item.status !== 'VOID' &&
          item.entryType === 'CLAWBACK' &&
          item.relatedCommissionId === row.id
      )
      .reduce((sum, item) => sum + Math.abs(Number(item.commissionAmount) || 0), 0);
    const remaining = (Number(row.commissionAmount) || 0) - clawedBack;
    return remaining > 0 ? remaining : 0;
  };

  const addModalToggle = () => {
    setAddModal(!addModal);
    setRateInputs({});
    setAddError('');
  };

  const clawbackModalToggle = (row?: IDHPlacementCommission) => {
    if (row) {
      setClawbackRow(row);
      // toFixed keeps float dust (e.g. 3333.3300000000004) out of the input.
      setClawbackAmount(remainingClawback(row).toFixed(2));
      setClawbackError('');
      return;
    }
    setClawbackRow(null);
    setClawbackAmount('');
    setClawbackError('');
  };

  const createCommissionHandler = async () => {
    const entries = splitOptions
      .map((option) => {
        const input = rateInputs[option.value];
        return {
          option,
          type: (input?.type ?? 'PERCENTAGE') as CommissionRateType,
          raw: (input?.value ?? '').trim(),
        };
      })
      .filter((entry) => entry.raw !== '');
    if (!entries.length) {
      setAddError('Enter a commission rate for at least one user.');
      return;
    }
    for (const entry of entries) {
      const value = parseFloat(entry.raw);
      if (entry.type === 'FLAT_RATE') {
        if (isNaN(value) || value <= 0) {
          setAddError(
            `Flat rate for ${entry.option.label} must be greater than zero.`
          );
          return;
        }
      } else if (isNaN(value) || value < 0 || value > 100) {
        setAddError(
          `Commission rate for ${entry.option.label} must be a number between 0 and 100.`
        );
        return;
      }
    }
    setAddError('');
    setBatchSaving(true);

    let created = 0;
    const failures: Array<string> = [];
    // Rows with an empty rate are simply skipped, so partial entry is fine.
    for (const entry of entries) {
      try {
        const response = await _createCommission({
          variables: {
            input: {
              placementId,
              userId: entry.option.userId,
              commissionSide: entry.option.side,
              rateType: entry.type,
              commissionRate:
                entry.type === 'PERCENTAGE' ? parseFloat(entry.raw) : null,
              flatAmount:
                entry.type === 'FLAT_RATE' ? parseFloat(entry.raw) : null,
            },
          },
        });
        const payload = response?.data?.createDHPlacementCommission;
        if (isSuccessResponse(payload?.status)) {
          created += 1;
        } else {
          failures.push(`${entry.option.label}: ${payload?.message || 'failed'}`);
        }
      } catch (catchError: any) {
        failures.push(`${entry.option.label}: ${catchError?.message || 'failed'}`);
      }
    }

    setBatchSaving(false);
    if (created) {
      toast.success(
        created === 1
          ? 'Commission added successfully'
          : `${created} commissions added successfully`
      );
    }
    await refetch();
    if (failures.length) {
      setAddError(failures.join(' · '));
      return;
    }
    addModalToggle();
  };

  const clawbackCommissionHandler = async () => {
    if (!clawbackRow) return;

    const remaining = remainingClawback(clawbackRow);
    const trimmedAmount = clawbackAmount?.trim();
    const parsedAmount = parseFloat(trimmedAmount);

    if (trimmedAmount && (isNaN(parsedAmount) || parsedAmount <= 0)) {
      setClawbackError('Clawback amount must be greater than zero.');
      return;
    }
    if (trimmedAmount && parsedAmount > remaining) {
      setClawbackError(
        `Clawback amount cannot exceed ${moneyFormatter(remaining)}.`
      );
      return;
    }
    setClawbackError('');

    try {
      const response = await _clawbackCommission({
        variables: {
          input: {
            commissionId: clawbackRow.id,
            amount: trimmedAmount ? parsedAmount : null,
          },
        },
      });

      const payload = response?.data?.clawbackDHPlacementCommission;
      if (isSuccessResponse(payload?.status)) {
        toast.success(payload?.message || 'Clawback recorded successfully');
        await refetch();
        clawbackModalToggle();
        return;
      }
      toast.error(payload?.message || 'Unable to record clawback');
    } catch (catchError: any) {
      toast.error(catchError?.message || 'Unable to record clawback');
    }
  };

  const voidCommissionHandler = async () => {
    if (!voidRow) return;

    try {
      const response = await _voidCommission({
        variables: { id: voidRow.id },
      });

      const payload = response?.data?.voidDHPlacementCommission;
      if (isSuccessResponse(payload?.status)) {
        toast.success(payload?.message || 'Commission entry voided');
        await refetch();
        setVoidRow(null);
        return;
      }
      toast.error(payload?.message || 'Unable to void commission entry');
    } catch (catchError: any) {
      toast.error(catchError?.message || 'Unable to void commission entry');
    }
  };

  const actionsColumn = (row: IDHPlacementCommission) => {
    if (!isExecutive) return null;
    return (
      <td>
        <div className="d-flex align-items-center">
          {row.entryType === 'COMMISSION' && (
            <button
              type="button"
              className="bg-transparent border-0 p-0 mr-3"
              style={{
                opacity:
                  row.status !== 'EARNED' || remainingClawback(row) <= 0
                    ? 0.5
                    : 1,
              }}
              onClick={() => clawbackModalToggle(row)}
              disabled={row.status !== 'EARNED' || remainingClawback(row) <= 0}
              title={
                row.status === 'EARNED'
                  ? 'Clawback'
                  : 'Only an earned commission can be clawed back'
              }
              aria-label="Clawback"
            >
              <ReplyIcon color="#0E79B6" size={16} />
            </button>
          )}
          <button
            type="button"
            className="bg-transparent border-0 p-0"
            onClick={() => setVoidRow(row)}
            title="Void"
            aria-label="Void"
          >
            <CrossOutlinedIcon size={16} />
          </button>
        </div>
      </td>
    );
  };

  const clawbackLine = (row: IDHPlacementCommission) => (
    <tr key={row.id}>
      <td>
        <h6 className="pl-4 mb-0">&#8627; Clawback</h6>
      </td>
      <td>
        <h6 className="mb-0">--</h6>
      </td>
      <td>
        <h6 className="mb-0 text-danger">
          {moneyFormatter(Number(row.commissionAmount) || 0)}
        </h6>
      </td>
      <td>
        <Badge color={statusColor(row.status)} className="text-capitalize" style={{ marginLeft: '-13px' }}>
          {statusLabel(row.status)}
        </Badge>
      </td>
      {actionsColumn(row)}
    </tr>
  );

  // Each side gets its own table, mirroring the Account Manager / Recruiters
  // Split section above — with equal-width columns so nothing crowds.
  const columnWidth = isExecutive ? '20%' : '25%';

  const sideTable = (side: CommissionSide) => {
    const sideGroups = groups.filter((group) => group.side === side);
    return (
      <>
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="mb-1">
            {side === 'AM' ? 'Account Manager Commission' : 'Recruiters Commission'}
          </h5>
        </div>

        <div>
          {sideGroups.length ? (
            <div className="dark-table">
              <Table
                className="transparentTable"
                style={{ borderCollapse: 'separate' }}
              >
                <thead>
                  <tr>
                    <th style={{ width: columnWidth }}>Name</th>
                    <th style={{ width: columnWidth }}>Rate</th>
                    <th style={{ width: columnWidth }}>Amount</th>
                    <th style={{ width: columnWidth }}>Status</th>
                    {isExecutive && (
                      <th style={{ width: columnWidth }}>Actions</th>
                    )}
                  </tr>
                </thead>

                {sideGroups.map((group) => (
                  <tbody key={group.key}>
                    {group.parents.map((node) => (
                      <Fragment key={node.row.id}>
                        <tr>
                          <td>
                            <h6 className="mb-0">{group.userName}</h6>
                          </td>
                          <td>
                            <h6 className="mb-0">
                              {node.row.rateType === 'FLAT_RATE'
                                ? 'Flat'
                                : `${node.row.commissionRate}%`}
                            </h6>
                          </td>
                          <td>
                            <h6 className="mb-0">
                              {moneyFormatter(
                                Number(node.row.commissionAmount) || 0
                              )}
                            </h6>
                          </td>
                          <td>
                            <Badge
                              color={statusColor(node.row.status)}
                              className="text-capitalize"
                              style={{ marginLeft: '-13px' }}
                            >
                              {statusLabel(node.row.status)}
                            </Badge>
                          </td>
                          {actionsColumn(node.row)}
                        </tr>
                        {node.clawbacks.map((row) => clawbackLine(row))}
                      </Fragment>
                    ))}

                    {group.orphanClawbacks.map((row) => clawbackLine(row))}

                    {group.hasClawback && (
                      <tr>
                        <td colSpan={2}>
                          <h6 className="mb-0 font-weight-bold">
                            Net — {group.userName}
                          </h6>
                        </td>
                        <td>
                          <h6
                            className={`mb-0 font-weight-bold ${group.net < 0 ? 'text-danger' : ''
                              }`}
                          >
                            {moneyFormatter(group.net)}
                          </h6>
                        </td>
                        <td />
                        {isExecutive && <td />}
                      </tr>
                    )}
                  </tbody>
                ))}
              </Table>
            </div>
          ) : (
            <div className="emptyPlaceholder">
              <img src={EMPTY_ICON} alt="empty" />
              {side === 'AM'
                ? 'Commission for account manager is not entered yet'
                : 'Commission for recruiter is not entered yet'}
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="mt-3">
      <div className="jobOrderRevamp">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="mb-1">Direct Hire Commissions</h5>

          <div className="d-flex align-items-center">
            {isExecutive && (
              <button
                type="button"
                className="buttonGenericStyle filledButton"
                onClick={() => setAddModal(true)}
                disabled={!splitOptions.length || !hasNetCommission}
              >
                Add Commission
              </button>
            )}
          </div>
        </div>

        {isExecutive && !hasNetCommission && (
          <small className="text-danger d-block mb-2">
            Net commission is not set on this placement, commissions cannot be
            entered yet.
          </small>
        )}

        {error && (
          <Alert color="danger">
            Unable to load commissions. {error?.message}
          </Alert>
        )}

        {loading && !rows.length ? (
          <Loader loaderClass="sm-height" />
        ) : (
          <Row>
            <Col lg="5" md="6" sm="12" xs="12">
              {sideTable('AM')}
            </Col>

            <Col lg="2" md="6" sm="12" xs="12"></Col>

            <Col lg="5" md="6" sm="12" xs="12">
              {sideTable('RECRUITING')}
            </Col>
          </Row>
        )}

      </div>

      {isExecutive && (
        <Modal
          isOpen={addModal}
          toggle={addModalToggle}
          className="revampDialog revampDialogWidth"
        >
          <ModalHeader toggle={addModalToggle}>
            <div className="d-flex align-items-center">
              <span>Add Commission</span>
            </div>
            <button
              className="iconButton"
              onClick={addModalToggle}
              type="button"
            >
              <img src={CLOSE_ICON} alt="close" />
            </button>
          </ModalHeader>

          <ModalBody>
            {(['AM', 'RECRUITING'] as Array<CommissionSide>).map((side) => {
              const sideOptions = splitOptions.filter(
                (option) => option.side === side
              );
              if (!sideOptions.length) return null;
              return (
                <div key={side} className="mb-3">
                  <h6 className="mb-1 font-weight-bold">
                    {side === 'AM' ? 'Account Managers' : 'Recruiters'}
                  </h6>
                  <h6 className="mb-2">
                    Base: {moneyFormatter((Number(netCommission) || 0) / 2)}
                  </h6>
                  {sideOptions.map((option) => {
                    const rowInput = rateInputs[option.value];
                    return (
                      <Row
                        key={option.value}
                        className="align-items-center mb-2"
                      >
                        <Col md="4">
                          <h6 className="mb-0">{option.userName}</h6>
                        </Col>
                        <Col md="3">
                          <Input
                            type="select"
                            name={`rateType-${option.value}`}
                            value={rowInput?.type ?? 'PERCENTAGE'}
                            onChange={(e) =>
                              setRateInputs({
                                ...rateInputs,
                                [option.value]: {
                                  type: e.target.value as CommissionRateType,
                                  value: rowInput?.value ?? '',
                                },
                              })
                            }
                          >
                            <option value="PERCENTAGE">Percentage</option>
                            <option value="FLAT_RATE">Flat Rate</option>
                          </Input>
                        </Col>
                        <Col md="3">
                          <InputGroup className="m-0">
                            <InputGroupAddon addonType="prepend">
                              {(rowInput?.type ?? 'PERCENTAGE') === 'FLAT_RATE'
                                ? '$'
                                : '%'}
                            </InputGroupAddon>
                            <Input
                              placeholder={
                                (rowInput?.type ?? 'PERCENTAGE') ===
                                'FLAT_RATE'
                                  ? 'Amount'
                                  : 'Rate'
                              }
                              name={`commissionRate-${option.value}`}
                              type="number"
                              min={0}
                              value={rowInput?.value ?? ''}
                              onChange={(e) =>
                                setRateInputs({
                                  ...rateInputs,
                                  [option.value]: {
                                    type: rowInput?.type ?? 'PERCENTAGE',
                                    value: e.target.value,
                                  },
                                })
                              }
                            />
                          </InputGroup>
                        </Col>
                        <Col md="2" className="text-right">
                          <h6 className="mb-0">
                            {moneyFormatter(previewFor(rowInput))}
                          </h6>
                        </Col>
                      </Row>
                    );
                  })}
                </div>
              );
            })}

            <small className="d-block text-muted mt-2">
              Rows left blank are skipped. Final amounts are calculated on the
              server from the placement's current fee and splits.
            </small>

            {!!addError && (
              <small className="text-danger d-block mt-2">{addError}</small>
            )}
          </ModalBody>

          <ModalFooter>
            <button
              type="button"
              onClick={addModalToggle}
              className="buttonPrimary mr-3"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={createCommissionHandler}
              className="buttonGenericStyle filledButton"
              disabled={batchSaving || createLoading}
            >
              {(batchSaving || createLoading) && (
                <Spinner size="sm" className="mr-2" />
              )}
              Save
            </button>
          </ModalFooter>
        </Modal>
      )}

      {isExecutive && (
        <Modal
          isOpen={!!clawbackRow}
          toggle={() => clawbackModalToggle()}
          className="revampDialog revampDialogWidth"
        >
          <ModalHeader toggle={() => clawbackModalToggle()}>
            <div className="d-flex align-items-center">
              <span>Clawback Commission</span>
            </div>
            <button
              className="iconButton"
              onClick={() => clawbackModalToggle()}
              type="button"
            >
              <img src={CLOSE_ICON} alt="close" />
            </button>
          </ModalHeader>

          <ModalBody>
            <Row>
              <Col md="12">
                <h6 className="mb-1">
                  {clawbackRow?.user?.fullName || '--'} —{' '}
                  {clawbackRow ? sideLabel(clawbackRow.commissionSide) : '--'}
                </h6>
                <h6 className="mb-3">
                  Commission:{' '}
                  {moneyFormatter(Number(clawbackRow?.commissionAmount) || 0)} ·
                  Available to claw back:{' '}
                  {moneyFormatter(remainingClawback(clawbackRow))}
                </h6>
              </Col>

              <Col md="12">
                <Label>
                  Clawback Amount <span className="text-danger">*</span>
                </Label>
                <InputGroup className="m-0">
                  <InputGroupAddon addonType="prepend">$</InputGroupAddon>
                  <Input
                    placeholder="Clawback Amount"
                    name="clawbackAmount"
                    type="number"
                    min={0}
                    value={clawbackAmount}
                    onChange={(e) => setClawbackAmount(e.target.value)}
                  />
                </InputGroup>
                <small className="d-block text-muted">
                  Recorded as a negative entry, partial clawbacks are allowed.
                </small>
              </Col>

              {!!clawbackError && (
                <Col md="12">
                  <small className="text-danger d-block mt-2">
                    {clawbackError}
                  </small>
                </Col>
              )}
            </Row>
          </ModalBody>

          <ModalFooter>
            <button
              type="button"
              onClick={() => clawbackModalToggle()}
              className="buttonPrimary mr-3"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={clawbackCommissionHandler}
              className="buttonGenericStyle filledButton"
              disabled={clawbackLoading}
            >
              {clawbackLoading && <Spinner size="sm" className="mr-2" />}
              Clawback
            </button>
          </ModalFooter>
        </Modal>
      )}

      {isExecutive && (
        <ConfirmPopUp
          confirmText={`Are you sure you want to void this commission entry for ${voidRow?.user?.fullName || 'this user'
            }?`}
          isOpen={!!voidRow}
          toggle={() => setVoidRow(null)}
          confirmAction={voidCommissionHandler}
          modalHeading="Void Commission Entry"
          btnText="Void"
          btnColor="primary"
          loading={voidLoading}
          disabled={voidLoading}
          className="revampDialog revampDialogWidth"
        />
      )}
    </div>
  );
};

export default DHPlacementCommissions;
